import { useRef } from 'react';

import { t } from '../i18n';
import {
  ActionMenu,
  ActionMenuInput,
  ActionMenuItem,
  ActionMenuSub,
  ActionMenuSubContent,
  ActionMenuSubTrigger,
} from './ActionMenu';

export interface BlockTurnItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  current: boolean;
}

export interface BlockColorItem {
  id: string;
  label: string;
  current: boolean;
}

export interface BlockMenuAction {
  key: string;
  label: string;
  glyph: string;
  hint: string;
  danger?: boolean;
  run: () => void;
}

interface BlockActionMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the menu hangs: the editor positions this next to the block's grip. */
  anchorStyle: React.CSSProperties;
  query: string;
  onQueryChange: (query: string) => void;
  onEmptyBackspace: () => void;
  onCloseFocus: () => void;
  currentKindLabel: string;
  turnItems: BlockTurnItem[] | null;
  onTurn: (id: string) => void;
  textColors: BlockColorItem[] | null;
  backgroundColors: BlockColorItem[] | null;
  onTextColor: (id: string) => void;
  onBackgroundColor: (id: string) => void;
  actions: BlockMenuAction[];
  footer: string | null;
}

function Tick({ shown }: { shown: boolean }): React.ReactElement {
  return (
    <span className="selbar__tick" aria-hidden="true">
      {shown ? '✓' : ''}
    </span>
  );
}

export function BlockActionMenu({
  open,
  onOpenChange,
  anchorStyle,
  query,
  onQueryChange,
  onEmptyBackspace,
  onCloseFocus,
  currentKindLabel,
  turnItems,
  onTurn,
  textColors,
  backgroundColors,
  onTextColor,
  onBackgroundColor,
  actions,
  footer,
}: BlockActionMenuProps): React.ReactElement {
  const search = useRef<HTMLInputElement>(null);
  const hasColors = textColors !== null && backgroundColors !== null;
  const isEmpty = turnItems === null && !hasColors && actions.length === 0;

  return (
    <ActionMenu
      open={open}
      onOpenChange={onOpenChange}
      label={t('editor.blockOptions')}
      className="blk-menu"
      align="start"
      rootClassName="blk-menu-root"
      // The anchor stands in for the grip, which keeps its own press and drag handling.
      trigger={<span className="blk-menu-anchor" style={anchorStyle} aria-hidden="true" />}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        search.current?.focus();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        // After an outside click focus already sits on whatever was clicked; leave it there.
        const active = document.activeElement;
        const menu = event.target;
        if (active === document.body || (menu instanceof Node && menu.contains(active))) {
          onCloseFocus();
        }
      }}
    >
      <ActionMenuInput
        ref={search}
        type="text"
        className="ni blk-menu__search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder={t('editor.searchActions')}
        aria-label={t('editor.searchActions')}
        onKeyDown={(event) => {
          if ((event.key !== 'Backspace' && event.key !== 'Delete') || query !== '') return;
          event.preventDefault();
          onEmptyBackspace();
        }}
      />

      {turnItems ? (
        <ActionMenuSub>
          <ActionMenuSubTrigger className="blk-menu__item nb">
            <span className="blk-menu__glyph" aria-hidden="true">
              ⇄
            </span>
            <span className="blk-menu__name">{t('editor.turnInto')}</span>
            <span className="blk-menu__right">
              <span>{currentKindLabel}</span>
              <span aria-hidden="true">›</span>
            </span>
          </ActionMenuSubTrigger>
          <ActionMenuSubContent label={t('editor.turnInto')} className="blk-menu__flyout">
            {turnItems.map((item) => (
              <ActionMenuItem
                key={item.id}
                className={item.current ? 'blk-menu__item nb is-on' : 'blk-menu__item nb'}
                onSelect={() => onTurn(item.id)}
              >
                {item.icon}
                <span className="blk-menu__name">{item.label}</span>
                <Tick shown={item.current} />
              </ActionMenuItem>
            ))}
          </ActionMenuSubContent>
        </ActionMenuSub>
      ) : null}

      {textColors && backgroundColors ? (
        <ActionMenuSub>
          <ActionMenuSubTrigger className="blk-menu__item nb">
            <span className="blk-menu__glyph" aria-hidden="true">
              ◐
            </span>
            <span className="blk-menu__name">{t('editor.color')}</span>
            <span className="blk-menu__right" aria-hidden="true">
              ›
            </span>
          </ActionMenuSubTrigger>
          <ActionMenuSubContent label={t('editor.color')} className="blk-menu__flyout">
            <p className="blk-menu__label">{t('editor.textColor')}</p>
            {textColors.map((swatch) => (
              <ActionMenuItem
                key={`tc-${swatch.id || 'default'}`}
                className="blk-menu__item nb"
                onSelect={() => onTextColor(swatch.id)}
              >
                <span
                  className="blk-menu__swatch-text"
                  style={swatch.id ? { color: `var(--color-blk-text-${swatch.id})` } : undefined}
                  aria-hidden="true"
                >
                  A
                </span>
                <span className="blk-menu__name">{swatch.label}</span>
                <Tick shown={swatch.current} />
              </ActionMenuItem>
            ))}
            <p className="blk-menu__label">{t('editor.background')}</p>
            {backgroundColors.map((swatch) => (
              <ActionMenuItem
                key={`bg-${swatch.id || 'default'}`}
                className="blk-menu__item nb"
                onSelect={() => onBackgroundColor(swatch.id)}
              >
                <span
                  className="blk-menu__swatch-bg"
                  style={swatch.id ? { background: `var(--color-blk-bg-${swatch.id})` } : undefined}
                  aria-hidden="true"
                />
                <span className="blk-menu__name">{swatch.label}</span>
                <Tick shown={swatch.current} />
              </ActionMenuItem>
            ))}
          </ActionMenuSubContent>
        </ActionMenuSub>
      ) : null}

      {actions.length > 0 ? (
        <>
          <div className="blk-menu__sep" aria-hidden="true" />
          {actions.map((action) => (
            <ActionMenuItem
              key={action.key}
              className={action.danger ? 'blk-menu__item nb is-danger' : 'blk-menu__item nb'}
              onSelect={action.run}
            >
              <span className="blk-menu__glyph" aria-hidden="true">
                {action.glyph}
              </span>
              <span className="blk-menu__name">{action.label}</span>
              {action.hint ? <span className="blk-menu__hint">{action.hint}</span> : null}
            </ActionMenuItem>
          ))}
        </>
      ) : null}

      {isEmpty ? <div className="blk-menu__empty">{t('editor.noResults')}</div> : null}

      {footer ? <div className="blk-menu__foot">{footer}</div> : null}
    </ActionMenu>
  );
}
